import React, { Component, useState } from 'react';
import Select from 'react-select/async';
import { components } from 'react-select';
import PropTypes from 'prop-types';
import { isNil, is, head, propEq } from 'ramda';
import { isPresent } from 'utils/HelperMethods';

import cn from 'clsx';
import styles from './TagAsyncSelect.module.css';
import customStyles from './styles.js';

const CustomGroup = (props) => {
  const { children, data, selectProps } = props;
  const [isOpen, setIsOpen] = useState(false);

  const isRoot = propEq('root', 'id', head(data.options));

  const handleGroupToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const handleGroupClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (selectProps.onChange) {
      selectProps.onChange(data, { option: 'select-option' });
    }
  };

  return (
    <div>
      <components.Option {...props} innerProps={{ ...props.innerProps, onClick: handleGroupClick }}>
        <div className={styles.groupOption}>
          {children && !isRoot && (
            <>
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div onClick={handleGroupToggle} className={styles.groupToggle}>
                {isOpen ? '▼' : '▶'}
              </div>
            </>
          )}
          <div>{data.fullTitle}</div>
        </div>
      </components.Option>
      {isOpen && children}
    </div>
  );
};

const disableRootContainerScroll = () => {
  const client = document.querySelector('.client');

  if (client) {
    client.style.overflow = 'hidden';
  }
};

const enableRootContainerScroll = () => {
  const client = document.querySelector('.client');

  if (client) {
    client.style.removeProperty('overflow');
  }
};

class TagAsyncSelect extends Component {
  constructor(props) {
    super(props);

    this.state = {
      defaultOptions: null,
      isLoading: null,
    };
  }

  getOptionValue(option) {
    const { getOptionValue } = this.props;

    if (option === null) {
      return null;
    }

    return getOptionValue ? getOptionValue(option) : option.value;
  }

  getCurrentLabel = () => {
    const { value, isMulti } = this.props;

    if (isNil(value)) {
      return null;
    }

    return isMulti ? value.map(this.getOptionLabel).join(', ') : this.getOptionLabel(value);
  };

  getOptionLabel = (value) => {
    const { getOptionLabel } = this.props;

    return isNil(getOptionLabel) ? value.label : getOptionLabel(value);
  };

  rootStyles = () => {
    const { isDisabled, errors, noBottom } = this.props;
    return cn([styles.root], { [styles.disabled]: isDisabled, [styles.error]: errors, [styles.noBottom]: noBottom });
  };

  customStyles = () => {
    const { openMenuOnClick } = this.props;
    const noIdicatior = () => ({
      display: 'none',
    });

    return {
      ...customStyles,
      dropdownIndicator: openMenuOnClick ? customStyles.dropdownIndicator : noIdicatior,
    };
  };

  handleChange = (option, data) => {
    const { action } = data;
    const { onChange, onValueChange } = this.props;

    if (action === 'clear') {
      enableRootContainerScroll();
    }

    if (onChange) {
      onChange(option);
    }

    if (onValueChange) {
      const value = this.getOptionValue(option);
      onValueChange(value);
    }
  };

  handleLoadOptionsAfterMenuOpen = () => {
    const { loadOptions } = this.props;
    const cb = (options) => {
      this.setState({ defaultOptions: options, isLoading: false });
    };

    this.setState({ isLoading: true });

    const options = loadOptions('', cb);

    if (is(Promise, options)) {
      options.then(cb);
    }
  };

  handleMenuOpen = (...args) => {
    const { onMenuOpen, menuIsOpen, shouldLoadOptionsAfterMenuOpen } = this.props;

    if (isNil(menuIsOpen) || menuIsOpen) {
      disableRootContainerScroll();
    }

    if (shouldLoadOptionsAfterMenuOpen) {
      this.handleLoadOptionsAfterMenuOpen();
    }

    if (onMenuOpen) {
      onMenuOpen(...args);
    }
  };

  handleMenuClose = (...args) => {
    const { onMenuClose } = this.props;

    enableRootContainerScroll();

    if (onMenuClose) {
      onMenuClose(...args);
    }
  };

  render() {
    const { placeholder, inlinePlaceholder, errors, placeholder: innerPlaceholder, ...rest } = this.props;
    const errorsText = Array.isArray(errors) ? errors.join(', ') : errors;

    return (
      <div className={this.rootStyles()} title={this.getCurrentLabel()}>
        <div className={styles.info}>
          <div className={styles.placeholder}>{!inlinePlaceholder && placeholder}</div>
          <div className={styles.errorText}>{errorsText}</div>
        </div>
        <Select
          defaultOptions={this.state.defaultOptions}
          isLoading={this.state.isLoading}
          {...rest}
          innerPlaceholder={innerPlaceholder}
          errors={errors}
          styles={this.customStyles()}
          onChange={this.handleChange}
          placeholder={inlinePlaceholder && isPresent(placeholder) ? placeholder : ''}
          onMenuOpen={this.handleMenuOpen}
          onMenuClose={this.handleMenuClose}
          components={{ Group: CustomGroup }}
        />
      </div>
    );
  }
}

TagAsyncSelect.propTypes = {
  onChange: PropTypes.func,
  onValueChange: PropTypes.func,
  getOptionValue: PropTypes.func,
  placeholder: PropTypes.string,
  inlinePlaceholder: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  errors: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  noBottom: PropTypes.bool,
  openMenuOnClick: PropTypes.bool,
  onMenuOpen: PropTypes.func,
  onMenuClose: PropTypes.func,
  menuIsOpen: PropTypes.bool,
  getOptionLabel: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.shape(), PropTypes.arrayOf(PropTypes.shape())]),
  loadOptions: PropTypes.func,
  shouldLoadOptionsAfterMenuOpen: PropTypes.bool,
};

TagAsyncSelect.defaultProps = {
  openMenuOnClick: true,
  inlinePlaceholder: false,
  isMulti: false,
};

export default TagAsyncSelect;
